import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Users, TrendingUp, Percent, Euro, Calendar, UserCheck, UserMinus } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const RHDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await fetch(`${API}/rh/dashboard`, { credentials: 'include' });
      if (response.ok) {
        const result = await response.json();
        setData(result);
      }
    } catch (error) {
      console.error('Failed to fetch RH dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 w-64 bg-slate-200 rounded" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <div key={i} className="h-32 bg-slate-200 rounded-xl" />)}
        </div>
      </div>
    );
  }

  if (!data) {
    return <div className="text-center py-12 text-slate-500">Erreur de chargement</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 font-[Manrope]" data-testid="rh-dashboard-title">
            Tableau de Bord RH
          </h1>
          <p className="text-slate-500 mt-1">
            Suivi de vos agents commerciaux recrutes - {data.current_month}
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Commerciaux actifs</p>
                <p className="text-3xl font-bold text-slate-900">{data.num_active}</p>
                <p className="text-xs text-slate-400 mt-1">Rattaches a vous</p>
              </div>
              <div className="p-3 rounded-xl bg-indigo-100">
                <UserCheck className="w-6 h-6 text-indigo-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Taux commission</p>
                <p className="text-3xl font-bold text-emerald-600">{data.commission_rate}%</p>
                <p className="text-xs text-slate-400 mt-1">
                  {data.rh_thresholds
                    ? (data.num_active <= data.rh_thresholds.tier_1_max
                        ? `1-${data.rh_thresholds.tier_1_max} AC`
                        : data.num_active <= data.rh_thresholds.tier_2_max
                          ? `${data.rh_thresholds.tier_1_max + 1}-${data.rh_thresholds.tier_2_max} AC`
                          : `${data.rh_thresholds.tier_2_max}+ AC`)
                    : `${data.num_active} AC`}
                </p>
              </div>
              <div className="p-3 rounded-xl bg-emerald-100">
                <Percent className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">CA du mois</p>
                <p className="text-3xl font-bold text-slate-900">{data.monthly_ca.toLocaleString('fr-FR')} €</p>
                <p className="text-xs text-slate-400 mt-1">Genere par vos AC</p>
              </div>
              <div className="p-3 rounded-xl bg-blue-100">
                <TrendingUp className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Ma commission</p>
                <p className="text-3xl font-bold text-amber-600">{data.monthly_commission.toLocaleString('fr-FR', {minimumFractionDigits: 2})} €</p>
                <p className="text-xs text-slate-400 mt-1">Ce mois-ci</p>
              </div>
              <div className="p-3 rounded-xl bg-amber-100">
                <Euro className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Commercials Lists */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Active Commercials */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-[Manrope] flex items-center gap-2">
              <UserCheck className="w-5 h-5 text-emerald-600" />
              Commerciaux rattaches ({data.active_commercials.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.active_commercials.length === 0 ? (
              <p className="text-center py-6 text-slate-400">Aucun commercial rattache</p>
            ) : (
              <div className="space-y-3">
                {data.active_commercials.map((c) => (
                  <div key={c.user_id} className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                    <div>
                      <p className="font-medium text-slate-900">{c.name}</p>
                      <p className="text-sm text-slate-500">{c.email}</p>
                    </div>
                    <div className="text-right">
                      <Badge className="bg-emerald-100 text-emerald-700">Actif</Badge>
                      {c.dec_end_date && (
                        <p className="text-xs text-slate-400 mt-1">
                          Jusqu&apos;au {new Date(c.dec_end_date).toLocaleDateString('fr-FR')}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Introduced Commercials (DEC expired) */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-[Manrope] flex items-center gap-2">
              <UserMinus className="w-5 h-5 text-slate-400" />
              Introduits par vous ({data.introduced_commercials.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.introduced_commercials.length === 0 ? (
              <p className="text-center py-6 text-slate-400">Aucun commercial dans cette categorie</p>
            ) : (
              <div className="space-y-3">
                {data.introduced_commercials.map((c) => (
                  <div key={c.user_id} className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                    <div>
                      <p className="font-medium text-slate-900">{c.name}</p>
                      <p className="text-sm text-slate-500">{c.email}</p>
                    </div>
                    <div className="text-right">
                      <Badge variant="outline" className="text-slate-500">Expire</Badge>
                      {c.dec_end_date && (
                        <p className="text-xs text-slate-400 mt-1">
                          Termine le {new Date(c.dec_end_date).toLocaleDateString('fr-FR')}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Info Card */}
      <Card className="border-0 shadow-sm bg-indigo-50">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Calendar className="w-5 h-5 text-indigo-600 mt-0.5" />
            <div>
              <p className="font-medium text-slate-900">Bareme des commissions RH</p>
              <div className="grid grid-cols-3 gap-4 mt-2 text-sm" data-testid="rh-bareme-dynamic">
                <div>
                  <p className="font-semibold text-indigo-600">{data.rh_rates?.tier_1 ?? 7}%</p>
                  <p className="text-slate-600">1 a {data.rh_thresholds?.tier_1_max ?? 6} AC</p>
                </div>
                <div>
                  <p className="font-semibold text-indigo-600">{data.rh_rates?.tier_2 ?? 8}%</p>
                  <p className="text-slate-600">{(data.rh_thresholds?.tier_1_max ?? 6) + 1} a {data.rh_thresholds?.tier_2_max ?? 12} AC</p>
                </div>
                <div>
                  <p className="font-semibold text-indigo-600">{data.rh_rates?.tier_3 ?? 9}%</p>
                  <p className="text-slate-600">{data.rh_thresholds?.tier_2_max ?? 12}+ AC</p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
